import chartDashStyles from 'components/charts/constants/chartDashStyles';
import chartMarkerSymbols from 'components/charts/constants/chartMarkerSymbols';
import httpService from '../../services/http';
import chartOptionConstants from '../../components/charts/constants/chartOptionConstants';

// 404 = share revoked or expired; retrying only delays the redirect
export const retryUnless404 = (count: number, err: any) => err?.response?.status !== 404 && count < 3;

export type Simulation = {
  created_at: string;
  created_by: string;
  finished_at: string;
  fmu: number;
  folder: string;
  id: number;
  inputsFMU: Object;
  is_interactive: boolean;
  name: string;
  output_interval: number;
  parameters: Object;
  started_at: string;
  ui_setup_data: Object;
};

export type SvgData = {
  name: string;
  data: {
    name: string;
    data: {
      y: number[];
    };
  }[];
}[];

export type ResultData = {
  simulationId: number;
  [key: string]: { name: string; data: number[][] | SvgData }[] | number;
};

/**
 * @description Get simulation results by simulation ids
 */
export const getSimulationResults = async (ids: Array<number>): Promise<ResultData[]> => {
  const resultData: ResultData[] = [];
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    const response = await httpService.get(
      `simulations/${id}/results/`,
      {
        headers: null,
      },
      'json',
      true,
      'v2',
    );
    resultData.push({ simulationId: id, ...response.data });
  }
  return Promise.all(resultData);
};

/**
 * @description Get simulations by simulation ids
 */
export const getSimulations = async (ids: Array<number>): Promise<Simulation[]> => {
  const simulation = [];
  for (const id of ids) {
    // eslint-disable-next-line no-await-in-loop
    const response = await httpService.get(
      `simulations/${id}/`,
      {
        headers: null,
      },
      'json',
      true,
      'v2',
    );
    simulation.push(response.data);
  }
  return Promise.all<Simulation[]>(simulation);
};

/**
 * Derives the time-scrub range of a simulation result from the data itself.
 *
 * FMI results are stored on a fixed grid from start to stop time with one sample
 * per output interval, so the longest series determines the covered time span:
 * (samples - 1) * outputInterval. Replaces the removed Simulation.stop_time API field.
 */
export const getScrubRange = (result: ResultData | undefined, outputInterval: number): number => {
  if (!result || !outputInterval) return 0;
  let maxSamples = 0;
  Object.entries(result).forEach(([key, chartData]) => {
    if (key === 'simulationId' || !Array.isArray(chartData)) return;
    chartData.forEach((series: any) => {
      const data = series?.data;
      if (!Array.isArray(data)) return;
      if (data.length && Array.isArray(data[0]?.data?.y)) {
        // Interactive SVG shape: [{name, data: {y: number[]}}] - one sample per y entry
        data.forEach((svgSeries: any) => {
          maxSamples = Math.max(maxSamples, svgSeries?.data?.y?.length ?? 0);
        });
      } else {
        // Pair arrays, value arrays and vector frames: one sample per entry
        maxSamples = Math.max(maxSamples, data.length);
      }
    });
  });
  return maxSamples > 1 ? (maxSamples - 1) * outputInterval : 0;
};

/**
 * @description Get chart data
 */
const getChartData = (chartDefinition: any, simulations: Simulation[], simulationResults: ResultData[]) => {
  return simulations.flatMap((simulation) => {
    const result = simulationResults.find((r) => r.simulationId === simulation.id);
    const chartData = result?.[chartDefinition.name] || [];

    if (typeof chartData === 'number') return [];

    return chartData.map((element) => ({
      ...element,
      output_interval: simulation.output_interval,
      stop_time: getScrubRange(result, simulation.output_interval),
    }));
  });
};

/**
 * Get updated chart definition items (series, cycles, vectors) for multiple simulations
 */
const getChartDefinitionUpdatedItems = (
  itemName: string,
  chartDefinition: any,
  simulations: Simulation[],
  colors: any,
) =>
  simulations.flatMap((simulation, simulationIndex) =>
    chartDefinition[itemName].map((item: any, itemIndex: number) => {
      const index = itemIndex * simulations.length + simulationIndex;
      return {
        ...item,
        key: `${simulation.id} ${item.name}`,
        name: `${simulation.name} ${item.name}`,
        color: colors[index],
        dashtyle: chartDashStyles[index],
        markerSymbol: chartMarkerSymbols[index],

        // Possibly not needed
        varName: item.name,
        simName: simulation.name,
        nrSims: simulations.length,
      };
    }),
  );

/**
 * Returns an updated version of the chart definition, including chart data and enriched
 * with the necessary configuration, ready to be rendered by the chart components.
 *
 * If multiple simulations are being compared, the chart data will be merged.
 */
export const getUpdatedChartDefinition = (
  chartDefinition: any,
  simulations: Simulation[],
  simulationResults?: ResultData[],
) => {
  if (!simulationResults || simulationResults.length === 0) {
    return { ...chartDefinition, data: null };
  }
  // Interactive SVG: Each simulation gets its own chart
  if (chartDefinition.type === 'interactive_svg' && simulations.length > 1) {
    return simulations.map((simulation) => {
      const result = simulationResults.find((r) => r.simulationId === simulation.id);
      const chartData = result?.[chartDefinition.name];
      if (typeof chartData === 'number' || !chartData)
        return { ...chartDefinition, data: null, simulationId: simulation.id };
      return {
        ...chartDefinition,
        data: chartData.map((data) => ({
          ...data,
          output_interval: simulation.output_interval,
          stop_time: getScrubRange(result, simulation.output_interval),
        })),
        simulationId: simulation.id,
      };
    });
  }
  const chartData = simulationResults ? getChartData(chartDefinition, simulations, simulationResults) : null;

  if (simulations.length === 1) {
    return { ...chartDefinition, data: chartData };
  }

  // Handle Simulation Comparison - Different chart types are handled with different merging strategies

  const colors = (chartDefinition.color || []).concat(chartOptionConstants.colors);

  // Cartesian Chart: Merge series of multiple simulations into one chart
  if (Object.keys(chartDefinition).includes('series')) {
    const updatedSeries = getChartDefinitionUpdatedItems('series', chartDefinition, simulations, colors);
    return { ...chartDefinition, series: updatedSeries, data: chartData };
  }

  if (Object.keys(chartDefinition).includes('cycles')) {
    const updatedCycles = getChartDefinitionUpdatedItems('cycles', chartDefinition, simulations, colors);
    return { ...chartDefinition, cycles: updatedCycles, data: chartData };
  }

  if (Object.keys(chartDefinition).includes('vectors')) {
    const updatedVectors = getChartDefinitionUpdatedItems('vectors', chartDefinition, simulations, colors);
    return { ...chartDefinition, vectors: updatedVectors, data: chartData };
  }

  if (Object.keys(chartDefinition).includes('categories')) {
    const updatedCategories: any = [];
    const updatedData: any = [];

    simulations.map((simulation) => {
      const groups: any = [];
      const result = simulationResults ? simulationResults.find((r) => r.simulationId === simulation.id) : undefined;
      const data = result?.[chartDefinition.name] || [];
      const scrubRange = getScrubRange(result, simulation.output_interval);

      chartDefinition.categories.map((category: any) => {
        if (category.groups.length > 1) {
          Array.isArray(data) &&
            updatedData.push({
              output_interval: simulation.output_interval,
              stop_time: scrubRange,
              name: `${simulation.name} ${simulation.id}`,
              data: data?.filter((element: any) => element.name === category.name)[0]?.data,
            });
          category.groups.map((group: any) => groups.push({ ...group }));
          return null;
        }
        const modifiedData: any = [];

        Array.isArray(data) &&
          data
            ?.filter((element) => element.name === category.name)
            .map((element: any) => element.data.map((d: any) => modifiedData.push({ ...d, name: category.name })));

        groups.push({ name: category.name, variable: category.groups[0].variable });
        updatedData.push({
          name: `${simulation.name} ${simulation.id}`,
          output_interval: simulation.output_interval,
          stop_time: scrubRange,
          data: modifiedData,
        });
        return null;
      });

      updatedCategories.push({ name: `${simulation.name} ${simulation.id}`, groups });
      return null;
    });

    return { ...chartDefinition, categories: updatedCategories, data: updatedData };
  }

  return { ...chartDefinition, data: chartData };
};
